'use client';

import { datadogRumAddFeatureFlagEvaluation } from '../util/datadogRumAddFeatureFlagEvaluation';
import { envHasDevFlagsEnabled } from '../util/envHasDevFlagsEnabled';
import { useIsLDClientReady } from './useIsLDClientReady';
import { useLDCookieOverride } from './useLDCookieOverride';
import { useLDLocalStorageOverride } from './useLDLocalStorageOverride';
import { useLocationUrl } from '@analytics/react';
import { CookieValueTypes } from 'cookies-next';
import { LDClient } from 'launchdarkly-js-client-sdk';
import { LDFlagSet } from 'launchdarkly-js-sdk-common';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';

/**
 * This hook is used to create a proxy for the feature flags in development mode.
 * It will check if the feature flag is set in the cookies or in the URL.
 * If it is, it will return the value from the URL first or from the cookies.
 * If not, it will return the value from LaunchDarkly.
 */
export function useDevFlags<T extends LDFlagSet>(): T {
  const isClientReady = useIsLDClientReady();

  const { isCookieValuesSet, cookieValues } = useLDCookieOverride();
  const { isLocalStorageValuesSet, localStorageValues } =
    useLDLocalStorageOverride();

  const url = useLocationUrl();
  const [proxy, setProxy] = useState<T>({} as T);
  const client = useLDClient();

  useEffect(() => {
    if (
      envHasDevFlagsEnabled() &&
      isClientReady &&
      isCookieValuesSet &&
      isLocalStorageValuesSet
    ) {
      const ldFlags = client?.allFlags() ?? {};
      const devOverrideFlags = getDevOverrideFlags(
        ldFlags,
        url,
        cookieValues,
        localStorageValues,
      );
      setProxy(toFlagsProxy(client, devOverrideFlags));
    }
    // This effect should only run when the client is ready and the cookies are set.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClientReady, isCookieValuesSet, url, isLocalStorageValuesSet]);

  return proxy;
}

/**
 * This function retrieves the value of a URL parameter by its key.
 * It returns the value as a string, boolean, or null if the parameter is not found.
 *
 * @param key - The key of the URL parameter to retrieve.
 * @param url - The URL from which to retrieve the parameter value.
 * @returns The value of the URL parameter as a string, boolean, or null.
 */
function getURLParamValue(
  key: string,
  url: string | undefined,
): string | boolean | null {
  const params = url !== undefined ? new URL(url).searchParams : undefined;

  const paramValue = params?.get(key) ?? undefined;

  if (paramValue !== undefined) {
    const parsedValue =
      paramValue?.toLowerCase() === 'true' ||
      paramValue?.toLowerCase() === 'false'
        ? paramValue.toLowerCase() === 'true'
        : paramValue;

    return parsedValue;
  }
  return null;
}

/**
 * Gets the feature flags that are overridden by the URL, cookies, or local storage.
 * It checks the URL for query parameters, cookies for values, and local storage for values.
 * The function returns an object containing the overridden flags.
 *
 * @param ldFlags LaunchDarkly flags set
 * @param url current URL
 * @param cookieValues flag values from cookies
 * @param localStorageValues flag values from local storage
 * @returns LDFlagSet - an object containing the overridden flags
 */
function getDevOverrideFlags(
  ldFlags: LDFlagSet,
  url: string | undefined,
  cookieValues: Record<string, CookieValueTypes>,
  localStorageValues: Record<string, string | boolean>,
): LDFlagSet {
  const ldFlagKeys = Object.keys(ldFlags);
  const urlOverrideFlags = ldFlagKeys.reduce(
    (acc: Record<string, unknown>, key) => {
      const urlValue = getURLParamValue(key, url);
      if (urlValue !== null) {
        acc[key] = urlValue;
      }
      return acc;
    },
    {},
  );

  const cookieOverrideFlags = ldFlagKeys.reduce(
    (acc: Record<string, unknown>, key) => {
      if (key in cookieValues) {
        acc[key] = cookieValues[key];
      }
      return acc;
    },
    {},
  );

  const localStorageOverrideFlags = ldFlagKeys.reduce(
    (acc: Record<string, unknown>, key) => {
      if (key in localStorageValues) {
        acc[key] = localStorageValues[key];
      }
      return acc;
    },
    {},
  );

  return {
    ...localStorageOverrideFlags,
    ...cookieOverrideFlags,
    ...urlOverrideFlags,
  };
}

/**
 * This function is a transposition of a function provided by `launchdarkly-react-client-sdk`.
 *
 * https://github.com/launchdarkly/react-client-sdk/blob/b9aa68ff2d1a8392ab6dd75f7444dc82e07915f2/src/getFlagsProxy.ts#L55
 *
 * It creates a proxy for the flags object that will return the value from the URL or cookies if available
 * otherwise it will return the value from LaunchDarkly.
 *
 * If a URL or cookie value is available this proxy will also report the value to Datadog RUM.
 */
function toFlagsProxy<T extends LDFlagSet>(
  ldClient: LDClient | undefined,
  devOverrideFlags: LDFlagSet,
): T {
  const ldFlags = ldClient?.allFlags() ?? {};
  return new Proxy({ ...ldFlags, devOverrideFlags } as unknown as T, {
    // trap for reading a flag value
    get(target, prop, receiver) {
      const currentValue = Reflect.get(target, prop, receiver);

      // only process flag keys and ignore symbols and native Object functions
      if (
        typeof prop === 'symbol' ||
        !Object.keys(ldFlags).includes(prop as string)
      ) {
        return currentValue;
      }

      if (prop in devOverrideFlags) {
        datadogRumAddFeatureFlagEvaluation(prop, devOverrideFlags[prop]);
        return devOverrideFlags[prop];
      }

      return ldClient?.variation(prop);
    },
  });
}
